import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, Zap, Building2, ArrowLeft, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import api from "@/lib/api";
import { useBilling } from "@/components/BillingProvider";
import { useAuth } from "@/components/AuthProvider";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import SEO from "@/components/SEO";

const INDIVIDUAL_FEATURES = [
  "Workspaces y tableros ilimitados",
  "50 MB de almacenamiento por usuario",
  "Drag & drop fluido",
  "Card modal con labels, checklist y comments",
  "Light/Dark mode con glassmorphism",
  "Búsqueda y filtros avanzados",
];

const CORPORATE_FEATURES = [
  "Todo lo del plan Pro",
  "50 MB por usuario",
  "Mínimo 3 usuarios (agrega más cuando quieras)",
  "Panel de admin de organización",
  "Estadísticas de accesos y uso por miembro",
  "Chat interno del equipo (DMs + broadcast)",
  "Facturación centralizada",
  "Soporte prioritario",
];

export default function Pricing() {
  const { user } = useAuth();
  const { billing } = useBilling();
  const [currency, setCurrency] = useState("mxn"); // default MXN
  const [cycle, setCycle] = useState("monthly");
  const [seats, setSeats] = useState(3);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoInfo, setPromoInfo] = useState(null); // { code, name, type, discount_amount, final_amount, ... }
  const [validatingPromo, setValidatingPromo] = useState(false);
  const navigate = useNavigate();

  const indvKey = `individual_${cycle}_${currency}`;
  const corpKey = `corporate_${cycle}_${currency}`;

  const prices = {
    mxn: {
      individual: { monthly: 99, annual: 891, monthlyOriginal: 99 * 12 },
      corporate: { monthly: 72, annual: 734.4, monthlyOriginal: 72 * 12 },
      symbol: "MX$", label: "MXN",
    },
    usd: {
      individual: { monthly: 6, annual: 54, monthlyOriginal: 6 * 12 },
      corporate: { monthly: 4, annual: 40.8, monthlyOriginal: 4 * 12 },
      symbol: "US$", label: "USD",
    },
  };
  const p = prices[currency];
  const individualPrice = cycle === "monthly" ? p.individual.monthly : p.individual.annual;
  const corporatePerSeatPrice = cycle === "monthly" ? p.corporate.monthly : p.corporate.annual;
  const corporateTotal = corporatePerSeatPrice * seats;

  const startCheckout = async (planKey, qty = 1) => {
    if (!user) { navigate("/login"); return; }
    setLoadingPlan(planKey);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planKey,
        seats: qty,
        origin_url: window.location.origin,
        promo_code: promoCode.trim() || undefined,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el checkout");
      setLoadingPlan(null);
    }
  };

  const validatePromo = async () => {
    const code = promoCode.trim();
    if (!code) { setPromoInfo(null); return; }
    setValidatingPromo(true);
    try {
      // Validate against the default plan (individual monthly of current currency) — server also checks per-plan at checkout
      const planKey = `individual_${cycle}_${currency}`;
      const { data } = await api.post("/billing/promotions/validate", { code, plan_id: planKey });
      setPromoInfo(data);
      toast.success("Código válido");
    } catch (e) {
      setPromoInfo(null);
      toast.error(e?.response?.data?.detail || "Código no válido");
    } finally {
      setValidatingPromo(false);
    }
  };

  return (
    <div className="min-h-screen relative z-10" data-testid="pricing-page">
      <SEO titleKey="seo.pricing.title" descriptionKey="seo.pricing.description" path="/pricing" />
      <div className="max-w-6xl mx-auto p-6 md:p-12">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          data-testid="pricing-back"
        >
          <ArrowLeft className="h-4 w-4" /> Volver
        </button>

        <div className="text-center mt-6 space-y-3 max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-wider text-blue-500 font-semibold">Planes Sprintroll</p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Empieza gratis, escala cuando lo necesites</h1>
          <p className="text-muted-foreground">
            Plan gratuito con 2 workspaces, 2 tableros y 10 MB de almacenamiento.
            Sin tarjeta. Actualiza cuando lo necesites.
          </p>

          {billing?.subscription_status === "free" && (
            <p className="text-sm text-blue-600 dark:text-blue-300 font-medium">
              Estás en el <strong>Plan gratuito</strong>. Actualiza para tener workspaces, tableros y almacenamiento sin límite.
            </p>
          )}
          {billing?.subscription_status === "active" && (
            <p className="text-sm text-emerald-600 dark:text-emerald-300 font-medium">
              Tienes una suscripción activa: {billing.plan} · {billing.billing_cycle} · {billing.currency?.toUpperCase()}.
            </p>
          )}
        </div>

        {/* Controls */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <div className="glass-panel rounded-full p-1 inline-flex" role="tablist" data-testid="cycle-toggle">
            {[
              { k: "monthly", l: "Mensual" },
              { k: "annual", l: "Anual" },
            ].map(o => (
              <button
                key={o.k}
                data-testid={`cycle-${o.k}`}
                onClick={() => setCycle(o.k)}
                className={cn(
                  "px-4 py-1.5 text-sm rounded-full font-medium transition",
                  cycle === o.k ? "bg-blue-600 text-white shadow-sm" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {o.l}
                {o.k === "annual" && <span className="ml-1.5 text-[10px] uppercase tracking-wider opacity-90">−25%</span>}
              </button>
            ))}
          </div>

          <div className="glass-panel rounded-full p-1 inline-flex" data-testid="currency-toggle">
            {[
              { k: "mxn", l: "MXN" },
              { k: "usd", l: "USD" },
            ].map(o => (
              <button
                key={o.k}
                data-testid={`currency-${o.k}`}
                onClick={() => setCurrency(o.k)}
                className={cn(
                  "px-3 py-1.5 text-sm rounded-full font-medium transition",
                  currency === o.k ? "bg-slate-900 dark:bg-white dark:text-slate-900 text-white shadow-sm" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>

        {/* Promo code */}
        <div className="mt-6 max-w-md mx-auto" data-testid="promo-code-input-section">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => { setPromoCode(e.target.value.toUpperCase().replace(/\s/g, "")); setPromoInfo(null); }}
              onBlur={() => promoCode && !promoInfo && validatePromo()}
              placeholder="¿Tienes un código promocional?"
              className="flex-1 h-10 px-3 rounded-full bg-white/60 dark:bg-slate-900/40 border border-white/40 dark:border-white/10 text-sm font-mono placeholder:font-sans placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
              data-testid="promo-code-input"
            />
            <button
              type="button"
              onClick={validatePromo}
              disabled={!promoCode || validatingPromo}
              className="h-10 px-4 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50"
              data-testid="promo-code-apply"
            >
              {validatingPromo ? "Validando…" : "Aplicar"}
            </button>
          </div>
          {promoInfo && (
            <div className="mt-2 rounded-xl bg-emerald-50/70 dark:bg-emerald-900/20 border border-emerald-400/40 p-3 text-sm" data-testid="promo-info">
              <p className="font-medium text-emerald-700 dark:text-emerald-300">
                ✓ {promoInfo.code} · {promoInfo.name}
              </p>
              {promoInfo.description && <p className="text-xs text-emerald-700/80 dark:text-emerald-300/80 mt-0.5">{promoInfo.description}</p>}
              {promoInfo.applies_to_price && promoInfo.discount_amount > 0 && (
                <p className="text-xs mt-1 text-muted-foreground">
                  Descuento: <strong>-{promoInfo.discount_amount} {promoInfo.currency}</strong> · Total: <strong>{promoInfo.final_amount} {promoInfo.currency}</strong>
                </p>
              )}
              {promoInfo.type === "free_months" && <p className="text-xs mt-1 text-muted-foreground">Se activarán <strong>{promoInfo.value} meses gratis</strong> adicionales al pagar.</p>}
              {promoInfo.type === "bonus_months" && <p className="text-xs mt-1 text-muted-foreground">Recibirás <strong>{promoInfo.value} meses extra</strong> al pagar tu plan.</p>}
              {promoInfo.type === "extended_trial" && <p className="text-xs mt-1 text-muted-foreground">Extiende tu prueba a <strong>{promoInfo.value} días</strong>.</p>}
              {promoInfo.type === "lifetime" && (
                <p className="text-xs mt-1 text-muted-foreground">
                  {promoInfo.duration_unit === "forever"
                    ? <>Acceso <strong>de por vida</strong> sin costo.</>
                    : <>Acceso <strong>gratis por {promoInfo.value} {promoInfo.duration_unit === "years" ? "año(s)" : "mes(es)"}</strong>.</>}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Plans */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Pro */}
          <div className="glass-panel rounded-3xl p-7 flex flex-col" data-testid="plan-individual">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-xl bg-blue-500/15 grid place-items-center text-blue-500">
                <Zap className="h-5 w-5" />
              </div>
              <h3 className="font-display text-2xl font-semibold">Pro</h3>
            </div>
            <p className="text-sm text-muted-foreground mt-1">Perfecto para gestionar tu trabajo personal.</p>

            <div className="mt-6">
              <div className="flex items-baseline gap-2">
                <span className="font-display text-5xl font-semibold tracking-tight">{p.symbol}{individualPrice.toFixed(currency === "usd" ? 0 : 0)}</span>
                <span className="text-sm text-muted-foreground">/ {cycle === "monthly" ? "mes" : "año"}</span>
              </div>
              {cycle === "annual" && (
                <p className="text-xs text-muted-foreground mt-1 line-through">{p.symbol}{p.individual.monthlyOriginal}/año</p>
              )}
              <p className="text-xs text-muted-foreground mt-2">{cycle === "monthly" ? "Facturado mensualmente" : "Equivale a " + p.symbol + (individualPrice / 12).toFixed(2) + " /mes"}</p>
            </div>

            <ul className="mt-6 space-y-2 text-sm">
              {INDIVIDUAL_FEATURES.map(f => (
                <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-blue-500 mt-0.5 flex-none" /> {f}</li>
              ))}
            </ul>

            <Button
              data-testid="plan-individual-select"
              disabled={loadingPlan !== null}
              onClick={() => startCheckout(indvKey, 1)}
              className="mt-7 w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loadingPlan === indvKey ? <Loader2 className="h-4 w-4 animate-spin" /> : "Suscribirme"}
            </Button>
            <p className="text-xs text-center text-muted-foreground mt-2">Plan gratuito para siempre. Actualiza cuando lo necesites.</p>
          </div>

          {/* Corporate */}
          <div className="glass-panel rounded-3xl p-7 flex flex-col border-blue-500/30 ring-1 ring-blue-500/20" data-testid="plan-corporate">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-xl bg-slate-900/10 dark:bg-white/10 grid place-items-center">
                  <Building2 className="h-5 w-5" />
                </div>
                <h3 className="font-display text-2xl font-semibold">Corporativo</h3>
              </div>
              <span className="text-[10px] uppercase tracking-wider bg-blue-600 text-white px-2 py-1 rounded-full font-semibold">Popular</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">Para equipos a partir de 3 personas.</p>

            <div className="mt-6">
              <div className="flex items-baseline gap-2">
                <span className="font-display text-5xl font-semibold tracking-tight">{p.symbol}{corporatePerSeatPrice.toFixed(currency === "usd" ? 0 : 2).replace(".00","")}</span>
                <span className="text-sm text-muted-foreground">/ usuario · {cycle === "monthly" ? "mes" : "año"}</span>
              </div>
              {cycle === "annual" && <p className="text-xs text-muted-foreground mt-1">15% de descuento aplicado en anual</p>}
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Usuarios:</span>
                <Select value={String(seats)} onValueChange={(v) => setSeats(Number(v))}>
                  <SelectTrigger className="w-24 h-9 bg-white/60 dark:bg-slate-900/40" data-testid="corporate-seats">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[3, 5, 8, 10, 15, 20, 30, 50].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground ml-1">
                  Total: <strong>{p.symbol}{corporateTotal.toFixed(2)}</strong> / {cycle === "monthly" ? "mes" : "año"}
                </span>
              </div>
            </div>

            <ul className="mt-6 space-y-2 text-sm">
              {CORPORATE_FEATURES.map(f => (
                <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-blue-500 mt-0.5 flex-none" /> {f}</li>
              ))}
            </ul>

            <Button
              data-testid="plan-corporate-select"
              disabled={loadingPlan !== null}
              onClick={() => startCheckout(corpKey, seats)}
              className="mt-7 w-full bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 dark:text-slate-900 text-white"
            >
              {loadingPlan === corpKey ? <Loader2 className="h-4 w-4 animate-spin" /> : `Continuar con ${seats} usuarios`}
            </Button>
            <p className="text-xs text-center text-muted-foreground mt-2">A partir de 3 usuarios. Cancela en cualquier momento.</p>
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-10">
          Pagos procesados de forma segura con PayPal. Los precios mostrados son en {p.label}.
        </p>
      </div>
    </div>
  );
}
